/* eslint-disable import/no-cycle */
import { setRadishAmount, useOfficialStore } from '$/store/useOfficialStore';
import { Message, UserMessage } from '$/pages/RoboneoChatBoard/type';
import type { RadishAmountResult } from '$/store/useOfficialStore/types';

import { isNumber } from 'lodash';
import { roboneoApi } from './request';
import APP from './api';
import {
  CheckFileParams, UploadSignParams, GetRoomParams, ShareDetailResult, RadishConfig, UserRadishRecordItem,
  SubmitRoomParams, CanvasErrmsg, VIPInfoList, ResponseRoomItemModelList, ResponseRoomItemInfo, ResponseHistory,
  ResponseMarkPointInfo,
  FeedbackDealParams,
  FeedbackDealResponse,
} from './server.types';

export * from './server.types';

interface EditRoomTitle {
  error_code: number;
  parameter: {};

}
interface MessageList {
  error_code: number;
  parameter: {
    result: (Message | UserMessage)[];
  };

}

/**
 * 读取接口返回的总萝卜数，兼容后端在个人账号下仅用 total_amount 表示总余额。
 */
function getTotalRadishAmount(parameter: Partial<RadishAmountResult>) {
  const hasTotalAmount = parameter.total_amount !== undefined
    && parameter.total_amount !== null
    && parameter.total_amount !== '';
  const totalAmount = Number(parameter.total_amount);
  if (hasTotalAmount && Number.isFinite(totalAmount)) {
    return totalAmount;
  }
  return Number(parameter.amount) || 0;
}

/**
 * 读取团队总池子字段；缺失时不回退个人 total_amount，避免团队态展示串到个人余额。
 */
function getTeamRadishAmount(parameter: Partial<RadishAmountResult>) {
  if (parameter.team_available_coin_num === undefined || parameter.team_available_coin_num === null) {
    return undefined;
  }
  const teamAvailableAmount = Number(parameter.team_available_coin_num);
  return Number.isFinite(teamAvailableAmount) ? teamAvailableAmount : undefined;
}

/**
 * 读取当前上下文下用于业务校验的可用萝卜数；团队版优先校验成员个人可用额度。
 */
function getAvailableRadishAmount(parameter: Partial<RadishAmountResult>, isTeamContext: boolean) {
  const personalAvailableAmount = Number(parameter.personal_available_coin_num);
  if (isTeamContext && Number.isFinite(personalAvailableAmount)) {
    return personalAvailableAmount;
  }
  if (!isTeamContext) {
    return getTotalRadishAmount(parameter);
  }
  const teamAvailableAmount = getTeamRadishAmount(parameter);
  if (teamAvailableAmount !== undefined) {
    return teamAvailableAmount;
  }
  return Number(parameter.amount) || 0;
}

/**
 * 读取右上角展示用萝卜总数；个人态只看总萝卜，团队态使用团队总池子。
 */
function getDisplayRadishAmount(
  parameter: Partial<RadishAmountResult>,
  isTeamContext: boolean,
  fallbackAmount = 0,
) {
  const teamAvailableAmount = getTeamRadishAmount(parameter);
  if (isTeamContext) {
    return teamAvailableAmount ?? fallbackAmount;
  }
  return getTotalRadishAmount(parameter);
}

/**
 * 规范化额度查询结果：amount 始终表示当前操作者可用额度；团队态的展示总额保留在 team_available_coin_num。
 */
function normalizeRadishAmountResult(
  parameter: Partial<RadishAmountResult>,
  isTeamContext: boolean,
): RadishAmountResult {
  const explicitTeamAvailableAmount = getTeamRadishAmount(parameter);
  const hasPersonalAvailableAmount = parameter.personal_available_coin_num !== undefined
    && parameter.personal_available_coin_num !== null;
  const fallbackTeamAvailableAmount = isTeamContext && explicitTeamAvailableAmount === undefined && hasPersonalAvailableAmount
    ? Number(parameter.amount)
    : undefined;
  const teamAvailableAmount = explicitTeamAvailableAmount
    ?? (Number.isFinite(fallbackTeamAvailableAmount) ? fallbackTeamAvailableAmount : undefined);
  const currentAvailableAmount = getAvailableRadishAmount(parameter, isTeamContext);
  const normalizedResult: RadishAmountResult = {
    ...parameter,
    amount: currentAvailableAmount,
    ...(isTeamContext && teamAvailableAmount !== undefined ? { team_available_coin_num: teamAvailableAmount } : {}),
  };
  // 个人额度接口也可能返回团队同名字段；移除后避免下游把个人分项额度误判为当前可用余额。
  if (!isTeamContext) {
    delete normalizedResult.team_available_coin_num;
  }
  return normalizedResult;
}

// 获取标记点信息
export async function getMarkPointInfoList(params: { image_url: string; point: number[]; }):
Promise<ResponseMarkPointInfo> {
  const response = await roboneoApi.request({
    method: 'POST',
    data: {
      parameter: {
        ...params,
        path_scene: 'frameextractor',
      },
    },
    url: APP.roboneo.api.path,
  });
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data);
    } else {
      reject();
    }
  });
}
// 模型列表信息查询接口
export async function roomItemModelList(): Promise<ResponseRoomItemModelList> {
  const response = await roboneoApi.request({
    method: 'POST',
    data: {
      parameter: {
        path_scene: 'model_list',
      },
    },
    url: `${APP.roboneo.api.path}/model_list`,
  });
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data);
    } else {
      reject();
    }
  });
}
// 用户房间模型信息查询接口
export async function roomItemInfo(params: {
  room_id: string;
}): Promise<ResponseRoomItemInfo> {
  const response = await roboneoApi.request({
    method: 'POST',
    data: {
      parameter: {
        ...params,
        path_scene: 'room_item_info_query',
      },
    },
    url: APP.roboneo.api.path,
  });
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data);
    } else {
      reject();
    }
  });
}

// 测试会话消息详情接口mock接口
export async function cancelChat(params: any): Promise<ResponseHistory> {
  const response = await roboneoApi.request(
    {
      data: params,
      method: 'POST',
      url: APP.roboneo.api.path,
    },
  );
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data);
    } else {
      reject();
    }
  });
}

// 测试会话消息详情接口mock接口
export async function getHistoryMessage(params: any): Promise<ResponseHistory> {
  const response = await roboneoApi.request(
    {
      data: params,
      method: 'POST',
      url: `${APP.roboneo.api.path}/${params.parameter.path_scene}`,
    },
  );
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data as unknown as ResponseHistory);
    } else {
      console.log(response.data, 'Datadata');
      reject(response.data);
    }
  });
}

// 请求业务服务端提供的参数签名接口
export async function uploadSign(data: UploadSignParams) {
  const response = await roboneoApi.request({
    data,
    method: 'POST',
    url: APP.roboneo.api.path,
  });
  return response;
}

// 视频、图片检测是否合法
export async function checkFileApi(data: CheckFileParams) {
  const response = await roboneoApi.request({
    data,
    method: 'POST',
    url: APP.roboneo.api.path,
    timeout: 120 * 1000, // 2分钟
  });
  return response;
}

// 获取房间号
export async function getRoomId(params: GetRoomParams & Record<string, any>) {
  const response = await roboneoApi.request({
    method: 'POST',
    data: {
      parameter: {
        ...params,
        path_scene: 'createroom',
      },
    },
    url: APP.roboneo.api.path,
  });
  return response;
}

// 提交房间，内容区第一次添加时调用
export async function submitRoom(params: SubmitRoomParams) {
  const response = await roboneoApi.request({
    method: 'POST',
    data: {
      parameter: {
        ...params,
        path_scene: 'roomsubmit',
      },
    },
    url: APP.roboneo.api.path,
  });
  return response;
}

/**
 * 创建分享链接
 *
 * @export
 * @param {string} roomId
 * @return {*}
 */
export async function createShareLink(roomId: string): Promise<string> {
  const response = await roboneoApi.request({
    method: 'POST',
    data: {
      parameter: {
        room_id: roomId,
        path_scene: 'createshare',
      },
    },
    url: APP.roboneo.api.path,
  });
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data.parameter.short_link);
    } else {
      reject();
    }
  });
}

/**
 * 分享详情
 *
 * @export
 * @param {string} linkId
 * @return {*}
 */
export async function getShareDetail(shortLink: string, neoGroupId = '', options: {
  isInternal?: boolean;
  createFrom?: '' | 'user' | 'system';
} = {}): Promise<ShareDetailResult> {
  const response = await roboneoApi.request({
    method: 'POST',
    data: {
      parameter: {
        short_link: shortLink,
        path_scene: 'sharedetail',
        need_half_block: true,
        neo_group_id: neoGroupId,
        create_from: options.createFrom || '',
      },
    },
    url: APP.roboneo.api.path,
    isInternal: !!options.isInternal,
  });
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data.parameter);
    } else {
      reject(response);
    }
  });
}

/**
 * 检测房间是否属于当前用户
 */
export async function checkRoom(roomId: string) {
  const response = await roboneoApi.request({
    method: 'POST',
    data: {
      parameter: {
        room_id: roomId,
        path_scene: 'roomcheck',
        need_half_block: true,
      },
    },
    url: APP.roboneo.api.path,
  });
  return response;
}

// 编辑房间标题
export async function editRoomInfo(params: any): Promise<EditRoomTitle> {
  const response = await roboneoApi.request(
    {
      data: params,
      method: 'POST',
      url: APP.roboneo.api.path,
    },
  );
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data);
    } else {
      reject(response.data);
    }
  });
}

/**
 * 检查美叶额度
 *
 * @export
 * @return {*}
 */
export async function checkMeiyeAmount(): Promise<RadishAmountResult> {
  const officialStore = useOfficialStore.getState();
  const { checkRadishAmount } = officialStore;
  const isTeamContext = Boolean(officialStore.clientOId);
  if (checkRadishAmount) {
    const result = await checkRadishAmount();
    // 宿主返回额度时自动同步右上角展示总数，业务校验仍使用 normalizedResult.amount。
    if (result) {
      const normalizedResult = normalizeRadishAmountResult(result, isTeamContext);
      setRadishAmount(getDisplayRadishAmount(normalizedResult, isTeamContext, officialStore.radishAmount));
      return normalizedResult;
    }
    return result as unknown as RadishAmountResult;
  }

  const response = await roboneoApi.request(
    {
      data: { parameter: { path_scene: 'meiyequery' } },
      method: 'POST',
      url: APP.roboneo.api.path,
    },
  );
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      const normalizedResult = normalizeRadishAmountResult(response.data.parameter, isTeamContext);
      // 更新右上角展示总数，避免团队态被成员个人额度覆盖。
      setRadishAmount(getDisplayRadishAmount(normalizedResult, isTeamContext, officialStore.radishAmount));
      resolve(normalizedResult);
    } else {
      reject();
    }
  });
}

// 反查ppt的json
export async function getMessageList(params: any): Promise<MessageList> {
  const response = await roboneoApi.request(
    {
      data: params,
      method: 'POST',
      url: APP.roboneo.api.path,
    },
  );
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data);
    } else {
      reject();
    }
  });
}

/**
 * 功能定价的枚举接口
 *
 * @export
 * @return {*}
 */
export async function getFeaturePriceList(): Promise<{ menu_items: any[]; }> {
  const response = await roboneoApi.request(
    {
      data: { parameter: { path_scene: 'neomenu' } },
      method: 'POST',
      url: APP.roboneo.api.path,
    },
  );
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data.parameter);
    } else {
      reject();
    }
  });
}

/**
 * 萝卜赠送扣减明细查询接口
 */
export async function getRadishRecordList(recordType: 1 | 2, page: number, page_count: number): Promise<{ total: number ; record_list: UserRadishRecordItem[]; }> {
  const response = await roboneoApi.request(
    {
      data: {
        parameter: {
          path_scene: 'recordlist', record_type: recordType, page, page_count,
        },
      },
      method: 'POST',
      url: APP.roboneo.api.path,
    },
  );
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data.parameter);
    } else {
      reject();
    }
  });
}

/**
 * 初始化赠送萝卜接口
 */
export async function getInitConfigList(params?: { scene_from: 'landing_page'; }): Promise<RadishConfig> {
  const response = await roboneoApi.request(
    {
      data: { parameter: { path_scene: 'initconfig', ...(params || {}) } },
      method: 'POST',
      url: APP.roboneo.api.path,
    },
  );
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data.parameter);
    } else {
      reject();
    }
  });
}

/**
 * 画布操作余额不足查询错误信息
 */
export async function getErrorMsgByNoAmount(id: string): Promise<CanvasErrmsg> {
  const response = await roboneoApi.request({
    data: { parameter: { path_scene: 'sub_guide_query', right_id: id } },
    method: 'POST',
    url: APP.roboneo.api.path,
  });
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data.parameter);
    } else {
      reject();
    }
  });
}

// 获取商品vip信息
export async function getVipProductList(bizCode: string): Promise<VIPInfoList> {
  const response = await roboneoApi.request({
    data: { parameter: { path_scene: 'product_list', entrance_biz_code: bizCode } },
    method: 'POST',
    url: APP.roboneo.api.path,
  });
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data.parameter);
    } else {
      reject();
    }
  });
}

/**
 * 仲裁模式-反馈退钱
 * https://api-mock.meitu-int.com/project/1897/interface/api/216009
 *
 * @export
 * @param {FeedbackDealParams} params
 * @return {*}  {Promise<FeedbackDealResponse>}
 */
export async function feedbackDeal(params: FeedbackDealParams): Promise<FeedbackDealResponse> {
  const response = await roboneoApi.request({
    data: {
      parameter: {
        path_scene: 'popup',
        ...params,
      },
    },
    method: 'POST',
    url: APP.roboneo.api.path,
  });
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data.parameter);
    } else {
      reject();
    }
  });
}

/**
 * @param params 视频抽帧
 */

export async function fetchVideoFrameByOffset(params: { video_url: string;format: string;offset: number; }) {
  const response = await roboneoApi.request(
    {
      data: {
        parameter: {
          path_scene: 'video_frame',
          ...params,
        },
      },

      method: 'POST',
      url: APP.roboneo.api.path,
    },
  );
  return response.data.parameter;
}

/** 视频裁剪可能耗时较长，仅为 video_clip 请求延长超时。 */
export async function fetchVideonByDuration(params: { video_url: string;start_time: number;duration: number; }) {
  const response = await roboneoApi.request(
    {
      data: {
        parameter: {
          path_scene: 'video_clip',
          ...params,
          start_time: isNumber(params.start_time) ? params.start_time / 1000 : 1,
          duration: isNumber(params.start_time) ? params.duration / 1000 : 1,
        },
      },

      method: 'POST',
      url: APP.roboneo.api.path,
      timeout: 300 * 1000,
    },
  );
  return response.data.parameter;
}

// roboneo/conversation_workflow/result
export async function postWorkflowResult(params: any): Promise<any> {
  const response = await roboneoApi.request({
    data: {
      parameter: {
        path_scene: 'conversation_workflow_result',
        conversation_workflow_result_data: {
          ...params,
        },
      },
    },
    method: 'POST',
    url: APP.roboneo.api.path,
  });
  return new Promise((resolve, reject) => {
    if (response.data.error_code === 0) {
      resolve(response.data.parameter);
    } else {
      reject(response.data.error_msg);
    }
  });
}
